import { createContext, ReactNode, useContext, useEffect, useReducer, useRef } from 'react';
import Olympics from '../model/Olympics';
import { getAllDistricts, getOlympicsSettings } from '../api/content-api';
import { loginPage } from './fixed-routing';
import AuthContext from './auth-context';
import SnackbarContext from './snackbar-context';
import { useNavigate } from 'react-router-dom';
import District from '../model/District';
import { debugError, debugLog } from '../utils/debug';
import { getBuildVariant } from '../config/variant-profile';

type OlympicsContextType = {
	olympics: Olympics | null;
	districts: District[];
	isDistrictsLoading: boolean;
	districtFilterId: number;
	searchFilterValue?: string;
	topics: any[];
	declarations: any[];
	settingsVersion: number;
	refreshTablesTrigger: number;
	isRefreshing: boolean;
	setOlympics: (olympics: Olympics) => void;
	setDistrictFilterId: (id: number) => void;
	setSearchFilterValue: (value: string) => void;
	refresh: () => void;
	refreshTables: () => void;
	setIsRefreshing: (value: boolean) => void;
};

type ActionType = {
	type: string;
	value?: any;
};

const SET_OLYMPICS = 'setOlympics';
const SET_TOPICS = 'setTopics';
const SET_DECLARATIONS = 'setDeclarations';
const SET_SETTINGS_VERSION = 'setSettingsVersion';
const SET_DISTRICTS = 'setDistricts';
const SET_DISTRICTS_LOADING = 'setDistrictsLoading';
const SET_DISTRICT_FILTER_ID = 'setDistrictFilterId';
const SET_SEARCH_FILTER_VALUE = 'setSearchFilterValue';
const REFRESH_TABLES = 'refreshTables';
const SET_IS_REFRESHING = 'setIsRefreshing';

const INITIAL_OLYMPICS_STATE: OlympicsContextType = {
	olympics: null,
	districts: [],
	isDistrictsLoading: true,
	districtFilterId: -1,
	topics: [],
	declarations: [],
	settingsVersion: 0,
	refreshTablesTrigger: 0,
	isRefreshing: false,
	setOlympics: (olympics) => {},
	setDistrictFilterId: (id) => {},
	setSearchFilterValue: (value: string) => {},
	refresh: () => {},
	refreshTables: () => {},
	setIsRefreshing: (value: boolean) => {},
};

const olympicsReducer = (state: OlympicsContextType, action: ActionType): OlympicsContextType => {
	switch (action.type) {
		case SET_OLYMPICS: {
			return {
				...state,
				olympics: action.value,
			};
		}
		case SET_DISTRICTS: {
			return {
				...state,
				districts: action.value,
			};
		}
		case SET_DISTRICTS_LOADING: {
			return {
				...state,
				isDistrictsLoading: action.value,
			};
		}
		case SET_TOPICS: {
			return {
				...state,
				topics: action.value,
			};
		}
		case SET_DECLARATIONS: {
			return {
				...state,
				declarations: action.value,
			};
		}
		case SET_SETTINGS_VERSION: {
			return {
				...state,
				settingsVersion: action.value,
			};
		}
		case SET_DISTRICT_FILTER_ID: {
			return {
				...state,
				districtFilterId: action.value,
			};
		}
		case SET_SEARCH_FILTER_VALUE: {
			return {
				...state,
				searchFilterValue: action.value,
			};
		}
		case REFRESH_TABLES: {
			const newTrigger = (state.refreshTablesTrigger || 0) + 1;
			debugLog('REFRESH_TABLES reducer: old trigger:', state.refreshTablesTrigger, 'new trigger:', newTrigger);
			return {
				...state,
				refreshTablesTrigger: newTrigger,
				isRefreshing: true,
			};
		}
		case SET_IS_REFRESHING: {
			return {
				...state,
				isRefreshing: action.value,
			};
		}
		default:
			return state;
	}
};

export const OlympicsContextProvider = ({ children }: { children: ReactNode }) => {
	const [olympicsState, dispatch] = useReducer(olympicsReducer, INITIAL_OLYMPICS_STATE, undefined);
	const stateRef = useRef(olympicsState);
	const { token, logout, isStudent } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const navigate = useNavigate();

	useEffect(() => {
		stateRef.current = olympicsState;
	}, [olympicsState]);

	const setOlympics = (olympics: Olympics) => {
		dispatch({ type: SET_OLYMPICS, value: olympics });
	};

	const setDistrictFilterId = (id: number) => {
		dispatch({ type: SET_DISTRICT_FILTER_ID, value: id });
	};

	const setSearchFilterValue = (val: string) => {
		dispatch({ type: SET_SEARCH_FILTER_VALUE, value: val });
	};

	const refreshTables = () => {
		debugLog('refreshTables called, current trigger:', olympicsState.refreshTablesTrigger);
		dispatch({ type: REFRESH_TABLES });
		debugLog('refreshTables dispatched');
	};

	const setIsRefreshing = (value: boolean) => {
		dispatch({ type: SET_IS_REFRESHING, value });
	};

	const getDistricts = () => {
		dispatch({ type: SET_DISTRICTS_LOADING, value: true });
		getAllDistricts()
			.then((response) => {
				if (response.status === 200) {
					const districts = response.data.map((d: any) => District.fromApiResponse(d));
					dispatch({ type: SET_DISTRICTS, value: districts });
				} else {
					debugError('Olympics context response error:', response);
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				console.error(exception);
				setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
			})
			.finally(() => {
				dispatch({ type: SET_DISTRICTS_LOADING, value: false });
			});
	};

	// Helper function to parse version date from format "FE:20251120_1455" or "BE:20251120_1455"
	const parseVersionDate = (version: string): Date | null => {
		// Extract date part after colon: "20251120_1455"
		const match = version.match(/:\s*(\d{8}_\d{4})/);
		if (!match) return null;

		const dateStr = match[1]; // "20251120_1455"
		const year = parseInt(dateStr.substring(0, 4), 10);
		const month = parseInt(dateStr.substring(4, 6), 10) - 1; // Month is 0-indexed
		const day = parseInt(dateStr.substring(6, 8), 10);
		const hour = parseInt(dateStr.substring(9, 11), 10);
		const minute = parseInt(dateStr.substring(11, 13), 10);

		return new Date(year, month, day, hour, minute, 0);
	};

	// Helper function to check if two versions are within 2 minutes of each other
	const areVersionsCompatible = (feVersion: string, beVersion: string): boolean => {
		if (feVersion === 'FE:unknown' || beVersion === 'BE:unknown') return false;

		const feDate = parseVersionDate(feVersion);
		const beDate = parseVersionDate(beVersion);

		if (!feDate || !beDate) return false;

		const diffMs = Math.abs(feDate.getTime() - beDate.getTime());
		const twoMinutes = 2 * 60 * 1000; // 2 minutes in milliseconds

		return diffMs <= twoMinutes;
	};

	const refresh = () => {
		if (!isStudent) {
			getDistricts();
		}
		// First priority: Check token validity (this should have priority over version check)
		getOlympicsSettings(token)
			.then((response) => {
				if (response.status === 200) {
					const prevOlympics = stateRef.current.olympics;
					const prevTopicsCount = stateRef.current.topics?.length ?? 0;
					const prevDeclarationsCount = stateRef.current.declarations?.length ?? 0;
					const nextTopicsCount = response.data.topics?.length ?? 0;
					const nextDeclarationsCount = response.data.declarations?.length ?? 0;

					const settingsChanged =
						!!prevOlympics &&
						(prevOlympics.firstStagePublished !== (response.data.first_stage_published === 1) ||
							prevOlympics.secondAStagePublished !== (response.data.second_a_stage_published === 1) ||
							prevOlympics.secondBStagePublished !== (response.data.second_b_stage_published === 1) ||
							prevOlympics.secondBStageQualificationPublished !==
								(response.data.second_b_stage_qualification_published === 1) ||
							prevOlympics.secondBOralDescriptionPublished !==
								(response.data.second_b_oral_description_published === 1) ||
							prevOlympics.thirdAStagePublished !== (response.data.third_a_stage_published === 1) ||
							prevOlympics.thirdBStagePublished !== (response.data.third_b_stage_published === 1) ||
							prevTopicsCount !== nextTopicsCount ||
							prevDeclarationsCount !== nextDeclarationsCount);

					if (settingsChanged) {
						dispatch({ type: SET_SETTINGS_VERSION, value: (stateRef.current.settingsVersion || 0) + 1 });
					}

					// Token is valid, now check version compatibility
					// Read FE version from meta tag
					const feVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content') || 'FE:unknown';

					// Fetch BE version from API
					fetch('/wp-json/olympics/version')
						.then((res) => res.json())
						.then((data) => {
							const beVersion = data.version || 'BE:unknown';
							const beVariant = data.variant as string | undefined;
							const feVariant = getBuildVariant();

							if (beVariant && beVariant !== feVariant) {
								debugLog(
									`Variant mismatch: FE build=${feVariant}, BE=${beVariant}. Refreshing will not fix this — rebuild/redeploy matching variant.`,
								);
								setMsg({
									msg: `Niezgodność wariantu FE (${feVariant}) i BE (${beVariant}). Przebuduj frontend dla tego samego wariantu co backend.`,
								});
							}

							// Check if versions are compatible (within 2 minutes) - if they are, clear refresh_attempted parameters
							if (areVersionsCompatible(feVersion, beVersion)) {
								const urlParams = new URLSearchParams(window.location.search);
								if (urlParams.has('refresh_attempted') || urlParams.has('refresh_attempted_time')) {
									// Versions are compatible - clear refresh attempt parameters
									urlParams.delete('refresh_attempted');
									urlParams.delete('refresh_attempted_time');
									const newUrl = new URL(window.location.href);
									newUrl.search = urlParams.toString();
									window.history.replaceState({}, '', newUrl.toString());
									debugLog(
										`Versions compatible (FE=${feVersion}, BE=${beVersion}). Cleared refresh_attempted parameters.`,
									);
								}
							}

							// If both versions are present and NOT compatible (difference > 2 minutes), refresh the page
							if (
								feVersion !== 'FE:unknown' &&
								beVersion !== 'BE:unknown' &&
								!areVersionsCompatible(feVersion, beVersion)
							) {
								// Check if we already tried to refresh (prevent infinite loop)
								const urlParams = new URLSearchParams(window.location.search);
								const refreshAttempted = urlParams.get('refresh_attempted') === 'true';
								const refreshAttemptedTime = urlParams.get('refresh_attempted_time');

								// If refresh was attempted, check if 5 minutes have passed
								if (refreshAttempted && refreshAttemptedTime) {
									const timeSinceAttempt = Date.now() - parseInt(refreshAttemptedTime, 10);
									const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

									if (timeSinceAttempt <= fiveMinutes) {
										// Less than or equal 5 minutes since last attempt - log warning and continue
										debugLog(
											`Version mismatch detected but refresh already attempted ${Math.round(timeSinceAttempt / 1000)}s ago. FE=${feVersion}, BE=${beVersion}. Waiting 5 minutes before retry...`,
										);
										// Continue with normal flow
									} else {
										// More than 5 minutes have passed - allow refresh again
										const newUrl = new URL(window.location.href);
										newUrl.searchParams.set('refresh_attempted', 'true');
										newUrl.searchParams.set('refresh_attempted_time', Date.now().toString());
										debugLog(
											`Version mismatch detected: FE=${feVersion}, BE=${beVersion}. Retrying refresh after 5 minutes...`,
										);
										window.location.href = newUrl.toString();
										return; // Exit early, don't fetch Olympics settings again
									}
								} else if (!refreshAttempted) {
									// First attempt - add parameter to prevent infinite refresh loop
									const newUrl = new URL(window.location.href);
									newUrl.searchParams.set('refresh_attempted', 'true');
									newUrl.searchParams.set('refresh_attempted_time', Date.now().toString());
									debugLog(`Version mismatch detected: FE=${feVersion}, BE=${beVersion}. Refreshing page...`);
									window.location.href = newUrl.toString();
									return; // Exit early, don't fetch Olympics settings again
								} else {
									// refresh_attempted is true but no timestamp - add timestamp and continue
									const newUrl = new URL(window.location.href);
									newUrl.searchParams.set('refresh_attempted_time', Date.now().toString());
									window.history.replaceState({}, '', newUrl.toString());
									debugLog(
										`Version mismatch detected but refresh already attempted (no timestamp). FE=${feVersion}, BE=${beVersion}. Continuing...`,
									);
								}
							}

							// Continue with normal data processing (versions are compatible or unknown)
							dispatch({ type: SET_OLYMPICS, value: Olympics.fromApiResponse(response.data) });
							dispatch({ type: SET_TOPICS, value: response.data.topics ?? [] });
							dispatch({ type: SET_DECLARATIONS, value: response.data.declarations ?? [] });
						})
						.catch(() => {
							// If version check fails, continue with normal data processing
							dispatch({ type: SET_OLYMPICS, value: Olympics.fromApiResponse(response.data) });
							dispatch({ type: SET_TOPICS, value: response.data.topics ?? [] });
							dispatch({ type: SET_DECLARATIONS, value: response.data.declarations ?? [] });
						});
				} else {
					debugError('Olympics context response error:', response);
					setMsg({ msg: response.data });
				}
			})
			.catch((exception) => {
				debugError('Olympics context error:', exception);
				if (exception.response?.status === 401 || exception.response?.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
				}
			});
	};

	useEffect(() => {
		if (!isStudent) {
			getDistricts();
		} else {
			dispatch({ type: SET_DISTRICTS, value: [] });
			dispatch({ type: SET_DISTRICTS_LOADING, value: false });
		}

		refresh();

		// Set up periodic refresh of settings every 5 minutes (300000 ms)
		// This ensures settings stay up-to-date even if changed by other users/sessions
		const settingsIntervalId = setInterval(() => {
			refresh();
		}, 300000); // 5 minutes

		return () => clearInterval(settingsIntervalId);
	}, []);

	const contextValue: OlympicsContextType = {
		...olympicsState,
		setOlympics,
		setDistrictFilterId,
		setSearchFilterValue,
		refresh,
		refreshTables,
		setIsRefreshing,
	};
	return <OlympicsContext.Provider value={contextValue}>{children}</OlympicsContext.Provider>;
};

const OlympicsContext = createContext<OlympicsContextType>({
	...INITIAL_OLYMPICS_STATE,
});

export { OlympicsContext };
export default OlympicsContext;
